import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";

import { requestNotificationPermission } from "../../firebase";
import { registerNotificationToken } from "../../Model/notifications";

const NotificationPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [loading, setLoading] = useState(false);

  // iOS / Home Screen states
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent || "";
    const platform = navigator.platform || "";

    /*
     * Detect iPhone / iPad / iPod
     */
    const ios =
      /iPhone|iPad|iPod/i.test(ua) ||
      (platform === "MacIntel" &&
        navigator.maxTouchPoints > 1);

    /*
     * Detect if opened as Home Screen / PWA
     */
    const standalone =
      window.navigator.standalone === true ||
      (window.matchMedia &&
        window.matchMedia(
          "(display-mode: standalone)"
        ).matches);

    console.log("iOS:", ios);
    console.log("Standalone:", standalone);
    console.log("User Agent:", ua);

    setIsIOS(ios);
    setIsStandalone(standalone);

    /*
     * iPhone / iPad
     *
     * If NOT opened from Home Screen,
     * show Add to Home Screen instructions.
     */
    if (ios && !standalone) {
      console.log(
        "iOS detected - showing Add to Home Screen"
      );

      setShowPrompt(false);
      return;
    }

    /*
     * Desktop / Android / iOS Home Screen
     *
     * Check browser notification permission.
     */
    if (
      typeof Notification !== "undefined" &&
      Notification.permission === "default"
    ) {
      setShowPrompt(true);
    } else {
      setShowPrompt(false);
    }
  }, []);

  const handleEnableNotifications = async () => {
    try {
      setLoading(true);

      // Request browser permission and get FCM token
      const token =
        await requestNotificationPermission();

      if (!token) {
        console.log(
          "Notification permission/token unavailable."
        );

        setShowPrompt(false);
        return;
      }

      console.log("FCM Token:", token);

      // Register token in Website API
      const result =
        await registerNotificationToken(token);

      console.log(
        "Notification registration:",
        result
      );

      // Hide banner after successful registration
      setShowPrompt(false);

    } catch (error) {
      console.error(
        "Notification registration error:",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  const handleNotNow = () => {
    setShowPrompt(false);
  };

  /*
   * Don't render anything if prompt should not be shown
   */
  if (!showPrompt) {
    return null;
  }

  /*
   * Show iPhone/iPad instructions
   */
  const showIOSInstructions =
    isIOS && !isStandalone;

  return (
    <Box
      sx={{
        position: "fixed",

        bottom: {
          xs: 12,
          sm: 20,
        },

        left: {
          xs: 12,
          sm: "50%",
        },

        transform: {
          xs: "none",
          sm: "translateX(-50%)",
        },

        width: {
          xs: "calc(100% - 24px)",
          sm: "560px",
          md: "620px",
        },

        backgroundColor: "#f3f1eb",

        border: "1px solid rgba(0, 0, 0, 0.12)",

        boxShadow:
          "0 10px 35px rgba(0, 0, 0, 0.18)",

        zIndex: 99999,

        px: {
          xs: 2,
          sm: 3,
        },

        py: {
          xs: 2,
          sm: 2.5,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",

          alignItems: {
            xs: "flex-start",
            sm: "center",
          },

          gap: 2,
        }}
      >
        {/* Notification Icon */}
        <Box
          sx={{
            width: 42,
            height: 42,
            minWidth: 42,

            borderRadius: "50%",

            backgroundColor: "#1c1c1c",

            color: "#fff",

            display: "flex",

            alignItems: "center",

            justifyContent: "center",

            fontSize: "18px",
          }}
        >
          🔔
        </Box>

        {/* Notification Text */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Bell MT, serif",

              fontSize: {
                xs: "1.05rem",
                sm: "1.2rem",
              },

              lineHeight: 1.2,

              color: "#1c1c1c",

              mb: 0.5,
            }}
          >
            Stay updated with Leon Gallery
          </Typography>

          {showIOSInstructions ? (
            <>
              <Typography
                sx={{
                  fontSize: {
                    xs: "0.76rem",
                    sm: "0.82rem",
                  },

                  lineHeight: 1.45,

                  color: "rgba(0,0,0,0.62)",

                  mb: 1,
                }}
              >
                To receive notifications on your
                iPhone, first add Leon Gallery to
                your Home Screen.
              </Typography>

              <Typography
                sx={{
                  fontSize: {
                    xs: "0.75rem",
                    sm: "0.82rem",
                  },

                  lineHeight: 1.55,

                  color: "#1c1c1c",
                }}
              >
                <strong>1.</strong> Tap the{" "}
                <strong>Share</strong> button.
                <br />

                <strong>2.</strong> Select{" "}
                <strong>Add to Home Screen</strong>.
                <br />

                <strong>3.</strong> Tap{" "}
                <strong>Add</strong>.
                <br />

                <strong>4.</strong> Open Leon Gallery
                from your Home Screen.
                <br />

                <strong>5.</strong> Tap{" "}
                <strong>Allow Notifications</strong>.
              </Typography>
            </>
          ) : (
            <Typography
              sx={{
                fontSize: {
                  xs: "0.76rem",
                  sm: "0.82rem",
                },

                lineHeight: 1.45,

                color: "rgba(0,0,0,0.62)",
              }}
            >
              Get notified about new auctions,
              exhibitions and important events.
            </Typography>
          )}
        </Box>

        {/* Buttons */}
        <Box
          sx={{
            display: "flex",

            flexDirection: {
              xs: "column",
              sm: "row",
            },

            alignItems: {
              xs: "stretch",
              sm: "center",
            },

            gap: 0.5,
          }}
        >
          {showIOSInstructions ? (
            <Button
              onClick={handleNotNow}
              sx={{
                backgroundColor: "#1c1c1c",

                color: "#fff",

                fontSize: {
                  xs: "0.7rem",
                  sm: "0.74rem",
                },

                textTransform: "none",

                whiteSpace: "nowrap",

                px: 1.8,

                py: 0.9,

                borderRadius: 0,

                "&:hover": {
                  backgroundColor: "#000",
                },
              }}
            >
              Got it
            </Button>
          ) : (
            <>
              <Button
                onClick={
                  handleEnableNotifications
                }
                disabled={loading}
                sx={{
                  backgroundColor: "#1c1c1c",

                  color: "#fff",

                  fontSize: {
                    xs: "0.7rem",
                    sm: "0.74rem",
                  },

                  textTransform: "none",

                  whiteSpace: "nowrap",

                  px: 1.8,

                  py: 0.9,

                  borderRadius: 0,

                  "&:hover": {
                    backgroundColor: "#000",
                  },
                }}
              >
                {loading
                  ? "Enabling..."
                  : "Allow Notifications"}
              </Button>

              <Button
                onClick={handleNotNow}
                disabled={loading}
                sx={{
                  color: "#555",

                  fontSize: "0.72rem",

                  textTransform: "none",

                  whiteSpace: "nowrap",

                  minWidth: "auto",
                }}
              >
                Not now
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default NotificationPrompt;